import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import SlidebarBtn from './components/SlidebarBtn';
import classnames from 'classnames';
import useGetState from '@intelligence/hooks/useGetState';
import { round } from '@gp/utils/common';
import { getElementWidth, getElementHorizontalMargin } from './utils';
import { LIST_NODE_CLASS_NAME } from './consts';
import { SlidebarPropsType } from './types';
import { Direction } from './enums';
import './index.scss';

const DEFAULT_SLIDE_DISTANCES = [0];

const Slidebar: React.FC<SlidebarPropsType> = (props) => {
  const {
    listNodeClassName,
    itemNodeClassName,
    offsetDistances, // 额外偏移距离
    updateDependencies,
    scrollItemIntoView,
    className,
    style,
    children,
    milestoneLegendList = [],
    timelinesWidth,
  } = props;

  const compRef = useRef<HTMLDivElement>(null);
  const leftBtnRef = useRef<HTMLDivElement>(null);
  const rightBtnRef = useRef<HTMLDivElement>(null);
  const listNodeRef = useRef<any>(null);
  const listNodeWidthRef = useRef(0);

  const [isOverflow, setIsOverflow, getIsOverflow] = useGetState(false);
  const [slideDistances, setSlideDistances] = useState(DEFAULT_SLIDE_DISTANCES);
  const [slideIndex, setSlideIndex] = useState(0);

  const maxIndex = useMemo(() => slideDistances?.length ? slideDistances?.length - 1 : 0, [slideDistances?.length, milestoneLegendList]);

  const getSlideTransformStyle = useCallback(
    (slideDistances: number[], index: number): string => {
      let slideDistance: any = Number(slideDistances[index]);
      slideDistance = Number.isNaN(slideDistance) ? 0 : slideDistance;
      if (slideDistance > 0) {
        slideDistance = `-${slideDistance}`;
      }

      return `translateX(${slideDistance}px)`;
    },
    [milestoneLegendList],
  );

  const setTransformStyle = useCallback(
    (transformStyle: string) => {
      if (!transformStyle || !listNodeRef.current) return;
      listNodeRef.current.style.transform = transformStyle;
    },
    [milestoneLegendList],
  );

  const handleSlideBtnClick = useCallback(
    (direction: Direction) => {
      let newSlideIndex;
      if (direction === Direction.Left) {
        newSlideIndex = Math.max(slideIndex - 1, 0);
      } else {
        newSlideIndex = Math.min(slideIndex + 1, maxIndex);
      }

      setTransformStyle(getSlideTransformStyle(slideDistances, newSlideIndex));
      setSlideIndex(newSlideIndex);
    },
    [maxIndex, slideIndex, slideDistances, getSlideTransformStyle, setTransformStyle],
  );

  const getContainerNodeWidth = useCallback((): number => {
    if (!compRef.current) return 0;
    const compWidth = getElementWidth(compRef.current);
    const isOverflow = getIsOverflow();
    if (!isOverflow) return compWidth;
    let btnWidths = [leftBtnRef.current, rightBtnRef.current]
      .filter((element) => element)
      .map((element) => getElementWidth(element as Element))
      .reduce((current, next) => current + next, 0);
    btnWidths = Number.isNaN(btnWidths) ? 0 : btnWidths;
    return compWidth - btnWidths;
  }, [milestoneLegendList]);

  const calcIsOverflow = useCallback(
    (containerNodeWidth: number): boolean => {
      const listNodeWidth = listNodeWidthRef.current;
      if (listNodeWidth === undefined || listNodeWidth === null) return false;
      return listNodeWidth > containerNodeWidth;
    },
    [milestoneLegendList, listNodeWidthRef.current],
  );

  const getSlideDistances = useCallback(
    (containerNodeWidth: number, isOverflow: boolean): number[] => {
      if (!isOverflow) return DEFAULT_SLIDE_DISTANCES;
      const listNode = listNodeRef.current;
      const listNodeWidth = listNodeWidthRef.current;
      const distances = [...DEFAULT_SLIDE_DISTANCES];
      let itemNodes: HTMLCollection;
      let itemNode: Element;
      let itemNodeWidth = 0;
      let distance = 0;
      let lastDistance = 0;
      let last2Distance: number;
      let innerBoxWidth = 0;
      if (!listNode) return DEFAULT_SLIDE_DISTANCES;
      if (listNodeWidth === undefined || listNodeWidth === null) return DEFAULT_SLIDE_DISTANCES;
      itemNodes = listNode.children;

      for (let i = 0;i < itemNodes.length;i++) {
        const t = itemNodes[i];
        // @ts-ignore
        innerBoxWidth += t?.offsetWidth;
      }
      for (let i = 0;i < itemNodes.length;i++) {
        itemNode = itemNodes[i];
        if (!itemNode) continue;
        // REMARK: 一个按钮宽度就大于容器宽度的情况，把这一个按钮 就分为多页，每页的滑动宽度为容器的宽度，剩下的作为这一个按钮最后一页的滑动宽度
        itemNodeWidth = getElementWidth(itemNode) + getElementHorizontalMargin(itemNode);
        if (itemNodeWidth >= containerNodeWidth) {
          const pages = Math.floor(itemNodeWidth / containerNodeWidth);
          const remains = itemNodeWidth % containerNodeWidth;
          const subDistances = Array.from({ length: pages }).map((item, index) =>
            round(distances[distances.length - 1] + containerNodeWidth * (index + 1)),
          );
          const lastSubDistance = round(subDistances[subDistances.length - 1] + remains);
          subDistances.push(lastSubDistance);
          distances.splice(distances.length, 0, ...subDistances);
        } else {
          distance += itemNodeWidth;
          if (distance > containerNodeWidth) {
            lastDistance = distance - itemNodeWidth;
            distance = lastDistance + distances[distances.length - 1];
            distances.push(round(distance));
            distance = 0;
            i--;
          }
        }
      }
      last2Distance = distances[distances.length - 2] || 0;
      distances[distances.length - 1] = round(last2Distance + distance - (containerNodeWidth - lastDistance));
      /* if (offsetDistances) {
        distances[distances.length - 1] = round(
          distances[distances.length - 1] + offsetDistances,
        );
      } */
      if (offsetDistances) {
        const realInnerBoxWidth = innerBoxWidth + offsetDistances;
        // const lastScrollDistances = realInnerBoxWidth > containerNodeWidth ? realInnerBoxWidth % containerNodeWidth : 0;
        // distances[distances.length - 1] = Math.ceil(last2Distance + lastScrollDistances);
        distances[distances.length - 1] = Math.ceil(realInnerBoxWidth - containerNodeWidth);
      }
      return distances;
    },
    [offsetDistances, milestoneLegendList],
  );

  const update = useCallback(() => {
    window.setTimeout(() => {
      const containerNodeWidth = getContainerNodeWidth();
      const isOverflow = calcIsOverflow(containerNodeWidth);
      const slideDistances = getSlideDistances(containerNodeWidth - 60, isOverflow);
      setIsOverflow(isOverflow);
      setSlideIndex(0);
      setSlideDistances(slideDistances);
      setTransformStyle(getSlideTransformStyle(slideDistances, 0));
    }, 0);
  }, [
    getContainerNodeWidth,
    calcIsOverflow,
    getSlideDistances,
    getSlideTransformStyle,
    setTransformStyle,
    offsetDistances,
    milestoneLegendList,
  ]);

  useEffect(() => {
    update();

    window.addEventListener('resize', update, false);

    return () => {
      window.removeEventListener('resize', update, false);
    };
  }, [update, updateDependencies, milestoneLegendList]);

  useEffect(() => {
    const comp = compRef.current;
    let listNode: HTMLElement | null;
    let itemNodes: HTMLCollection;
    let itemNode: Element;
    let listNodeWidth = 0;
    if (!comp) return;
    listNode = comp.querySelector(`.${listNodeClassName}`);
    if (!listNode) return;
    if (!listNode?.classList?.contains(LIST_NODE_CLASS_NAME)) {
      listNode?.classList?.add(LIST_NODE_CLASS_NAME);
    }
    itemNodes = listNode?.children;
    for (let i = 0;i < itemNodes?.length;i++) {
      itemNode = itemNodes?.[i];
      if (!itemNode) continue;
      listNodeWidth += getElementWidth(itemNode) + getElementHorizontalMargin(itemNode);
    }
    listNodeRef.current = listNode;
    listNodeWidthRef.current = listNodeWidth;
  }, [listNodeClassName, updateDependencies, milestoneLegendList, compRef.current, timelinesWidth]);

  // 外部传进来让滚动到某个位置，计算滚动到第几段
  useEffect(() => {
    if (!scrollItemIntoView?.dependancie) return;
    const distance = scrollItemIntoView?.distance;
    if (Array.isArray(slideDistances) && distance > 0) {
      let newSlideIndex = 0;
      for (let i = 0, l = slideDistances?.length;i < l;i++) {
        const t = slideDistances?.[i];
        const nextT = slideDistances?.[i + 1];
        if (t <= distance && nextT && nextT >= distance) {
          newSlideIndex = i;
          break;
        } else if (i === l - 1) {
          newSlideIndex = maxIndex;
        }
      }
      setTransformStyle(getSlideTransformStyle(slideDistances, newSlideIndex));
      setSlideIndex(newSlideIndex);
    }
  }, [scrollItemIntoView?.dependancie, milestoneLegendList]);

  return (
    <div ref={compRef} style={style} className={classnames('common-slidebar', className)}>
      {isOverflow && (
        <div ref={leftBtnRef} className="common-slidebar-btn-content">
          <SlidebarBtn
            direction={Direction.Left}
            disabled={slideIndex <= 0}
            onClick={() => {
              handleSlideBtnClick(Direction.Left);
            }}
          />
        </div>
      )}
      <div className="common-slidebar-content">{children}</div>
      {isOverflow && (
        <div ref={rightBtnRef} className="common-slidebar-btn-content">
          <SlidebarBtn
            direction={Direction.Right}
            disabled={slideIndex >= maxIndex}
            onClick={() => {
              handleSlideBtnClick(Direction.Right);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(Slidebar);
